// Shortlist option cards (nsofar docs/33 §7.3 full option objects).
// Renders credentials for coordinator verification (telemedicine guidelines:
// name + registration always visible) and, when the SR is selectable and
// actions are live, a two-step inline "Select for family" per option.

import { useState, type JSX } from "react";
import type { ConsultOption } from "@hcm/core";
import { formatIstStamp } from "../../lib/consultOps.js";

interface Props {
  options: ConsultOption[];
  chosenOptionId?: string | null;
  selectable: boolean;
  pending: boolean;
  onSelect: (optionId: string) => void;
}

function OptionMeta({ o }: { o: ConsultOption }): JSX.Element {
  const creds = [o.qualifications.join(", "), o.years_experience != null ? `${o.years_experience} yrs` : null]
    .filter(Boolean).join(" · ");
  const reg = [o.registration_no, o.registration_council].filter(Boolean).join(" · ");
  return (
    <div className="min-w-0">
      <p className="text-sm font-medium text-slate-800" title={`nsofar doctor_id: ${o.doctor_id}`}>{o.full_name}
        <span className="ml-2 text-xs font-normal text-slate-500">{o.specialty}</span></p>
      {creds && <p className="text-xs text-slate-500">{creds}</p>}
      {reg && <p className="text-xs text-slate-400">Reg: {reg}</p>}
      {o.languages.length > 0 && <p className="text-xs text-slate-400">{o.languages.join(", ")}</p>}
      <p className="text-xs text-slate-600 mt-1">{formatIstStamp(o.datetime_ist)}</p>
    </div>
  );
}

export function ConsultOptionsList({ options, chosenOptionId, selectable, pending, onSelect }: Props): JSX.Element {
  const [confirming, setConfirming] = useState<string | null>(null);
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-slate-600">Shortlist options</h3>
      {options.map((o) => {
        const isChosen = chosenOptionId === o.option_id;
        return (
          <div key={o.option_id}
            className={`flex items-start justify-between gap-3 border rounded-lg p-3 ${isChosen ? "border-green-300 bg-green-50" : "border-slate-200"}`}>
            <OptionMeta o={o} />
            <div className="shrink-0 text-right space-y-1">
              {isChosen && <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Chosen</span>}
              {selectable && !isChosen && (
                confirming === o.option_id ? (
                  <div className="flex gap-2">
                    <button type="button" disabled={pending}
                      className="text-xs font-medium text-white bg-navy-700 hover:bg-navy-800 rounded px-2 py-1 disabled:opacity-50"
                      onClick={() => { onSelect(o.option_id); setConfirming(null); }}>
                      {pending ? "Sending…" : `Confirm ${o.full_name.split(" ")[0] ?? "select"}`}
                    </button>
                    <button type="button" className="text-xs text-slate-500 hover:underline" onClick={() => setConfirming(null)}>Cancel</button>
                  </div>
                ) : (
                  <button type="button" className="text-xs text-navy-800 hover:underline"
                    onClick={() => setConfirming(o.option_id)}>Select for family…</button>
                )
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
